import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { systemLabelKey } from "../../../catalogs/characterSystemCatalog";
import { normalizeDamageType } from "../../../catalogs/damageTypes";
import { getLocalizedInventoryItemName } from "../../../catalogs/inventoryCatalog";

import RollResultModal from "../RollResultModal";
import {
  getWeaponAttack,
  rollWeaponCritical,
  rollWeaponDamage,
} from "../../../calculations/weaponRolls";
import { useD20Roll } from "../../../hooks/useD20Roll";
import { useDismissiblePopover } from "../../../hooks/useDismissiblePopover";

const WEAPON_CATEGORIES = ["melee", "ranged"];

const formatModifier = (value) => {
  const modifier = Number(value) || 0;
  return modifier > 0 ? `+${modifier}` : modifier;
};

const Weapon = ({ character, onChange }) => {
  const { i18n, t } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";
  const getItemName = (item) => getLocalizedInventoryItemName(item, language);
  const [openPicker, setOpenPicker] = useState(null);
  const { popoverRef, triggerRef } = useDismissiblePopover(
    Boolean(openPicker),
    () => setOpenPicker(null)
  );
  const { result, roll, showResult, closeRoll } = useD20Roll();
  const items = character.inventory?.items ?? [];
  const weapons = items.filter(
    (item) => item.type?.toLowerCase() === "weapon"
  );
  const ammunitionItems = items.filter(
    (item) => item.type?.toLowerCase() === "ammunition"
  );
  const getSelectedAmmunition = (weapon) =>
    ammunitionItems.find((item) => item.id === weapon.ammunitionId);

  const toggleWeapon = (selectedWeapon) => {
    const updatedItems = items.map((item) =>
      item.id === selectedWeapon.id
        ? { ...item, isActive: !item.isActive }
        : item
    );

    onChange({
      ...character,
      inventory: {
        ...character.inventory,
        items: updatedItems,
      },
    });
  };

  const selectAmmunition = (selectedWeapon, ammunitionId) => {
    const updatedItems = items.map((item) =>
      item.id === selectedWeapon.id ? { ...item, ammunitionId } : item
    );

    setOpenPicker(null);
    onChange({
      ...character,
      inventory: {
        ...character.inventory,
        items: updatedItems,
      },
    });
  };

  const rollAttack = (weapon) => {
    const attack = getWeaponAttack(character.characteristics, weapon);
    const ammunition = getSelectedAmmunition(weapon);

    if (
      weapon.weaponType === "ranged" &&
      ammunition &&
      Number(ammunition.amount) > 0
    ) {
      const updatedItems = items.map((item) =>
        item.id === ammunition.id
          ? { ...item, amount: Math.max(0, Number(item.amount) - 1) }
          : item
      );

      onChange({
        ...character,
        inventory: {
          ...character.inventory,
          items: updatedItems,
        },
      });
    }

    roll({
      label: t("roll.attack", { name: getItemName(weapon) }),
      modifier: attack.totalModifier,
      modifierBreakdown: [
        { label: t(systemLabelKey("abilities", attack.abilityId)), value: attack.abilityModifier },
        { label: t("roll.baseAttack"), value: attack.baseAttackModifier },
        { label: t("roll.weaponModifier"), value: attack.weaponModifier },
      ],
    });
  };

  const rollDamage = (event, weapon) => {
    event.stopPropagation();
    const ammunition = getSelectedAmmunition(weapon);
    const damage = rollWeaponDamage(weapon, Math.random, ammunition);
    if (!damage) {
      return;
    }

    showResult({
      label: t("roll.damageRoll", { name: getItemName(weapon) }),
      diceLabel: `${damage.diceCount}d${damage.diceSides}`,
      rolls: damage.rolls,
      diceGroups: damage.diceGroups,
      modifier: damage.bonus,
      modifierBreakdown: damage.modifierBreakdown ?? [
        { label: t("roll.constantBonus"), value: damage.bonus },
      ],
      multiplier: 1,
      total: damage.total,
      details: [
        ...(weapon.damageType
          ? [{ label: t("roll.damageType"), value: t(systemLabelKey("damageTypes", normalizeDamageType(weapon.damageType))) }]
          : []),
        ...(ammunition?.damageType
          ? [{ label: t("roll.ammoDamageType"), value: t(systemLabelKey("damageTypes", normalizeDamageType(ammunition.damageType))) }]
          : []),
      ],
    });
  };

  const rollCritical = (event, weapon) => {
    event.stopPropagation();
    const critical = rollWeaponCritical(weapon);
    if (!critical) {
      return;
    }

    showResult({
      label: t("roll.criticalRoll", { name: getItemName(weapon) }),
      diceLabel: `${critical.diceCount}d${critical.diceSides}`,
      rolls: critical.rolls,
      modifier: critical.bonus,
      modifierBreakdown: [
        { label: t("roll.constantBonus"), value: critical.bonus },
      ],
      multiplier: 1,
      total: critical.total,
      details: weapon.damageType
        ? [{ label: t("roll.damageType"), value: t(systemLabelKey("damageTypes", normalizeDamageType(weapon.damageType))) }]
        : [],
    });
  };

  const handleAttackKeyDown = (event, weapon) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      rollAttack(weapon);
    }
  };

  return (
    <div className="weapon-table">
      <h2>{t("equipment.weapon")}</h2>
      <table className="weapon-equipment-table">
        <thead>
          <tr>
            <th>{t("equipment.name")}</th>
            <th>{t("equipment.mod")}</th>
            <th>{t("equipment.range")}</th>
            <th>{t("equipment.ammo")}</th>
            <th>{t("equipment.damage")}</th>
            <th>{t("equipment.crit")}</th>
            <th aria-label={t("equipment.actions")} />
          </tr>
        </thead>
        <tbody>
          {WEAPON_CATEGORIES.map((type) => {
            const label = t(systemLabelKey("weaponTypes", type));
            const categoryWeapons = weapons.filter(
              (weapon) => weapon.weaponType === type
            );
            const equippedWeapons = categoryWeapons.filter(
              (weapon) => weapon.isActive
            );

            return (
              <React.Fragment key={type}>
                <tr className="weapon-type">
                  <td className="section-name" colSpan={7}>
                    {label}
                  </td>
                </tr>
                {equippedWeapons.map((weapon) => (
                  <tr
                    aria-label={t("roll.attackAction", { name: getItemName(weapon) })}
                    className="weapon-attack-row"
                    key={weapon.id}
                    onClick={() => rollAttack(weapon)}
                    onKeyDown={(event) => handleAttackKeyDown(event, weapon)}
                    role="button"
                    tabIndex={0}
                  >
                    <td className="active-item">{getItemName(weapon)}</td>
                    <td>{formatModifier(weapon.modifier)}</td>
                    <td>{weapon.range || "-"}</td>
                    <td
                      className="weapon-ammunition-cell"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {weapon.weaponType === "ranged" ? (
                        <div className="weapon-ammunition-selector">
                          <span>
                            {getSelectedAmmunition(weapon)
                              ? `${getItemName(getSelectedAmmunition(weapon))} (${getSelectedAmmunition(weapon).amount})`
                              : t("common.none")}
                          </span>
                          <div className="armor-picker-wrapper">
                            <button
                              aria-expanded={openPicker === `ammo-${weapon.id}`}
                              aria-label={t("equipment.chooseAmmoFor", { name: getItemName(weapon) })}
                              className="armor-picker-button"
                              type="button"
                              onClick={(event) => {
                                triggerRef.current = event.currentTarget;
                                setOpenPicker(
                                  openPicker === `ammo-${weapon.id}`
                                    ? null
                                    : `ammo-${weapon.id}`
                                )
                              }}
                            >
                              &#8943;
                            </button>
                            {openPicker === `ammo-${weapon.id}` && (
                              <div className="armor-picker-menu" ref={popoverRef}>
                                <strong>{t("equipment.chooseAmmo")}</strong>
                                <button
                                  className={!weapon.ammunitionId ? "selected" : ""}
                                  type="button"
                                  onClick={() => selectAmmunition(weapon, "")}
                                >
                                  {t("common.none")}
                                </button>
                                {ammunitionItems.map((ammunition) => (
                                  <button
                                    aria-label={t("equipment.useAmmo", { ammo: getItemName(ammunition), weapon: getItemName(weapon) })}
                                    className={weapon.ammunitionId === ammunition.id ? "selected" : ""}
                                    key={ammunition.id}
                                    type="button"
                                    onClick={() =>
                                      selectAmmunition(weapon, ammunition.id)
                                    }
                                  >
                                    <span>{getItemName(ammunition)}</span>
                                    <span>{ammunition.amount}</span>
                                  </button>
                                ))}
                                {ammunitionItems.length === 0 && (
                                  <small>{t("equipment.noAmmo")}</small>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        "\u2014"
                      )}
                    </td>
                    <td
                      aria-label={t("roll.damageAction", { name: getItemName(weapon) })}
                      className="weapon-damage-cell"
                      onClick={(event) => rollDamage(event, weapon)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          rollDamage(event, weapon);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {weapon.damage || "-"}
                      {weapon.damageType ? ` ${t(systemLabelKey("damageTypes", normalizeDamageType(weapon.damageType)))}` : ""}
                    </td>
                    <td
                      aria-label={t("roll.criticalAction", { name: getItemName(weapon) })}
                      className="weapon-critical-cell"
                      onClick={(event) => rollCritical(event, weapon)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          rollCritical(event, weapon);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {weapon.crit || "-"}
                    </td>
                    <td>
                      <button
                        aria-label={t("equipment.unequip", { name: getItemName(weapon) })}
                        className="armor-remove-button"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleWeapon(weapon);
                        }}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
                {equippedWeapons.length === 0 && (
                  <tr>
                    <td className="active-item">{t("equipment.nothing")}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td />
                  </tr>
                )}
                <tr>
                  <td className="weapon-manage-label">{t("equipment.manageWeapons")}</td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td>
                    <div className="armor-picker-wrapper">
                      <button
                        aria-expanded={openPicker === type}
                        aria-label={t("equipment.chooseWeapons", { name: label })}
                        className="armor-picker-button"
                        type="button"
                        onClick={(event) => {
                          triggerRef.current = event.currentTarget;
                          setOpenPicker(openPicker === type ? null : type)
                        }}
                      >
                        &#8943;
                      </button>
                      {openPicker === type && (
                        <div className="armor-picker-menu" ref={popoverRef}>
                          <strong>{t("equipment.chooseWeapons", { name: label })}</strong>
                          {categoryWeapons.map((weapon) => (
                            <button
                              aria-label={t(weapon.isActive ? "equipment.unequip" : "equipment.equip", { name: getItemName(weapon) })}
                              className={weapon.isActive ? "selected" : ""}
                              key={weapon.id}
                              type="button"
                              onClick={() => toggleWeapon(weapon)}
                            >
                              <span>
                                {weapon.isActive ? "✓ " : ""}
                                {getItemName(weapon)}
                              </span>
                              <span>{weapon.damage || "-"}</span>
                            </button>
                          ))}
                          {categoryWeapons.length === 0 && (
                            <small>{t("equipment.noWeapons")}</small>
                          )}
                          <button
                            type="button"
                            onClick={() => setOpenPicker(null)}
                          >
                            {t("common.close")}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <RollResultModal result={result} onClose={closeRoll} />
    </div>
  );
};

export default Weapon;
